<template>
  <div>
    <b data-test="title" class="mb-sm text-sm text-black">{{ title }}</b>
    <div class="mt-sm flex justify-center sm:justify-between">
      <div class="flex flex-row justify-between">
        <TWDigit v-model="code[0]" :success="successDisplay" :show-input="currentIndex === 0" class="mr-xs xs:mr-base" />
        <TWDigit v-model="code[1]" :success="successDisplay" :show-input="currentIndex === 1" class="mr-xs xs:mr-base" />
        <TWDigit v-model="code[2]" :success="successDisplay" :show-input="currentIndex === 2" class="mr-xs xs:mr-base" />
      </div>
      <div class="leading-45 lg:leading-xl"><b>-</b></div>
      <div class="flex flex-row justify-between">
        <TWDigit v-model="code[3]" :success="successDisplay" :show-input="currentIndex === 3" class="ml-xs xs:ml-base" />
        <TWDigit v-model="code[4]" :success="successDisplay" :show-input="currentIndex === 4" class="ml-xs xs:ml-base" />
        <TWDigit v-model="code[5]" :success="successDisplay" :show-input="currentIndex === 5" class="ml-xs xs:ml-base" />
      </div>
    </div>
    <div class="w-full text-sm">
      <div
        v-if="computedErrorMessage"
        class="p-base mt-sm bg-warning2 text-whiteOverride text-center rounded-lg">
        <ExclamationTriangleIcon class="mr-base" />
        <span data-test="error" >{{ computedErrorMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';
import TWDigit from '@/components/tailwind/authentication/Digit.vue';
import { logAmplitudeEvent } from '@/utils/analytics';

export default {
  name: 'AuthCodeInput',
  components: {
    TWDigit,
    ExclamationTriangleIcon,
  },
  props: {
    errorMessage: String,
    successDisplay: Boolean,
    title: String,
  },
  computed: {
    currentIndex() {
      return this.code.length;
    },
    authCode() {
      return this.code.join('');
    },
    computedErrorMessage() {
      return this.errorMessage || this.internalErrorMessage;
    },
  },
  data() {
    return {
      code: [],
      internalErrorMessage: null,
    };
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyboardCodeInput);
    window.addEventListener('paste', e => this.pasteCode(e.clipboardData.getData('text')));
  },
  watch: {
    code: {
      deep: true,
      handler(newVal) {
        this.$emit('auth-code-changed', this.authCode);

        if (newVal.length === 1) {
          this.$emit('clear-error');
        }
      },
    },
    errorMessage(newVal) {
      if (newVal) {
        this.code = [];
      }
    },
  },
  methods: {
    pasteCode(code) {
      // strip out any non number chars (mostly in case they accidentally copy a space)
      const strippedCode = parseInt(code, 10);
      // turn back into string to measure length
      const stringCode = strippedCode.toString();
      // check if pasted number is the right length
      if (stringCode.length === 6) {
        // if there are already some numbers inputted, delete them because we want to paste the whole code
        if (this.code.length > 0) {
          this.code = [];
        }
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < stringCode.length; i++) {
          this.code.push(stringCode[i]);
          logAmplitudeEvent('auth_code_input--paste_successful');
        }
      } else {
        this.code = [];
        this.internalErrorMessage = this.$t('authentication.pasteError');
        logAmplitudeEvent('auth_code_input--paste_failed');
      }
    },
    handleKeyboardCodeInput(e) {
      // if key pressed is Backspace or Delete
      if ((e.key === 'Backspace') || (e.key === 'Delete')) {
        this.deleteCodeDigit();
      }

      // if key pressed is Enter
      if (e.key === 'Enter') {
        this.$emit('submit-auth-code');
      }

      logAmplitudeEvent('auth_code_input--keyboard_event');
    },
    deleteCodeDigit() {
      if (this.code.length > 0) {
        this.code.splice(-1);
      }
      // if an invalid code has been pasted, there will be no code to delete but we still want to use backspace/delete to clear error message
      this.internalErrorMessage = null;
      this.$emit('clear-error');
    },
  },
};
</script>

<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
